import { Injectable, OnInit } from '@angular/core';
import { CompanyDeductionConfig } from '../models/company-deduction-config';
import { CompanyEarningConfig } from '../models/company-earning-config';
import { WarningLevel } from '../models/error-detail';
import {
  EarningTotals,
  MasterPayrollObject,
  Payline,
  PayrollConfigSetupData,
  TaxHierarchy
} from '../models/payroll';
import { CompanyLocalTaxConfig, CompanyTaxCodeConfig } from '../models/tax-config';
import { MapBy, mergeSum } from '../utils/math';

@Injectable({
  providedIn: 'root'
})
export class PayrollSummaryService {
  constructor() {}

  public PayrollHasErrors(payroll: MasterPayrollObject) {
    let hasError = false;
    for (let pay of payroll.EmployeePays) {
      for (let line of pay.Paylines) {
        if (!line.OkToPay) {
          continue;
        }

        if (
          line.Errors &&
          line.Errors.filter(x => x.WarningLevel === WarningLevel.Critical).length
        ) {
          hasError = true;
          break;
        }
      }
      if (hasError) {
        break;
      }
    }

    return hasError;
  }

  public CalculatePayrollTotals(payroll: MasterPayrollObject, config: PayrollConfigSetupData) {
    let totals = new PayrollSummary();

    payroll.EmployeePays.forEach(pay => {
      pay.Paylines.forEach(line => {
        if (!line.OkToPay) {
          return;
        }

        let totalsSummary = this._calculateTotals(line);
        totals.concat(totalsSummary);

        let taxSummary = this._calculateTaxes(line);
        totals.concat(taxSummary);

        let earningsSummary = this._calculateEarnings(
          line,
          this.CreateEarningMaps(config.Earnings)
        );
        totals.concat(earningsSummary);

        let deductionSummay = this._calculateDeductions(
          line,
          this.CreateDeductionMaps(config.Deductions)
        );
        totals.concat(deductionSummay);
      });
    });

    return totals;
  }

  public GenerateOrderedLists(
    payrollSummary: PayrollSummary,
    configSetupData: PayrollConfigSetupData,
    masterTaxMap: TaxCodeAndType[]
  ) {
    var lists = new PayrollSummaryOrderedLists();

    // task 11590 - sort taxes
    lists.EmployerTaxes = this._sortTaxes(payrollSummary.Tax.EmployerTaxes, masterTaxMap);
    lists.EmployeeTaxes = this._sortTaxes(payrollSummary.Tax.EmployeeTaxes, masterTaxMap);

    let earningConfigMap = this.CreateEarningMaps(configSetupData.Earnings);
    let deductionConfigMap = this.CreateDeductionMaps(configSetupData.Deductions);

    // get list of unique values of earning/deduction names
    // sorted by appearence sequence
    let earnings = [];
    Object.entries(earningConfigMap).forEach(entry => {
      if (payrollSummary.Earnings.Names.includes(entry[1].Name)) {
        earnings.push({
          seq: earningConfigMap[entry[0]].AppearanceSequence,
          name: earningConfigMap[entry[0]].Name
        });
      }
    });

    lists.EarningsNames =
      payrollSummary.Earnings.Names.length > 1
        ? earnings.sort((a, b) => a.seq - b.seq).map(x => x.name)
        : payrollSummary.Earnings.Names;

    let employeeDeductions = [];
    let memoDeductions = [];
    Object.entries(deductionConfigMap).forEach(entry => {
      // check the totals collection. 0 sum deductions should not appear
      if (entry[1].Type == 1 && payrollSummary.Deductions.MemoItemDeductionTotals[entry[1].Name]) {
        memoDeductions.push({
          seq: deductionConfigMap[entry[0]].AppearanceSequence,
          name: entry[1].Name
        });
      } else if (payrollSummary.Deductions.EmployeeDeductionTotals[entry[1].Name]) {
        employeeDeductions.push({
          seq: deductionConfigMap[entry[0]].AppearanceSequence,
          name: entry[1].Name
        });
      }
    });

    lists.EmployeeDeductionNames = [...employeeDeductions]
      .sort((a: any, b: any) => a.seq - b.seq)
      .map((x: any) => x.name);

    lists.MemoDeductionNames = [...memoDeductions]
      .sort((a: any, b: any) => a.seq - b.seq)
      .map((x: any) => x.name);

    return lists;
  }

  // bug 7908 - tax sorting
  // sort order is federal codes, state codes (grouped also by their state) and then local codes
  // each section tax codes are sorted by
  // their tax hierarchy (see CP PAY's APS_TAX_TYPE_HIERARCHY tbl) and tax type, and then alphabetically
  public BuildMasterTaxMap(
    taxHierarchies: TaxHierarchy[],
    taxConfigs: CompanyTaxCodeConfig[],
    localTaxConfigs: CompanyLocalTaxConfig[]
  ) {
    // order tax hierarchy list
    taxHierarchies.sort((a, b) => {
      return a.Hierarchy - b.Hierarchy;
    });

    // build a map of federal and state company tax codes based on tax type
    let sortedFedAndStateTaxCodes: TaxCodeAndType[] = [];
    const tempFed: TaxCodeAndType[] = [];
    const tempState: TaxCodeAndType[] = [];

    // federal taxes (entity type "F")
    taxConfigs
      .filter(t => t.EntityType == 'F')
      .forEach(taxConfig => {
        tempFed.push(new TaxCodeAndType(taxConfig.TaxCode.trim(), taxConfig.TaxType));
      });

    let sortedFedTaxCodes = this._getSortedTaxList(tempFed, taxHierarchies);

    // state taxes (entity type "S")
    taxConfigs
      .filter(t => t.EntityType == 'S')
      .forEach(taxConfig => {
        tempState.push(new TaxCodeAndType(taxConfig.TaxCode.trim(), taxConfig.TaxType));
      });

    let sortedStateTaxCodes = this._getSortedTaxList(tempState, taxHierarchies);

    // group the state codes by state abbreviation
    sortedStateTaxCodes = sortedStateTaxCodes.sort((a, b) => {
      const aConfig = taxConfigs.find(x => x.TaxCode.trim() == a.TaxCode.trim());
      const bConfig = taxConfigs.find(x => x.TaxCode.trim() == b.TaxCode.trim());

      return aConfig.StateAbbreviation.localeCompare(bConfig.StateAbbreviation);
    });

    // combine sorted federal and state codes
    sortedFedAndStateTaxCodes = [...sortedFedTaxCodes.concat(...sortedStateTaxCodes)];

    // build a map of local company tax codes based on tax type
    const tempLocal: TaxCodeAndType[] = [];
    localTaxConfigs.forEach(localTaxConfig => {
      localTaxConfig.TaxCodes.forEach(localTaxCode => {
        tempLocal.push(new TaxCodeAndType(localTaxCode.Key.trim(), localTaxConfig.TaxType));
      });
    });

    let sortedLocalTaxCodes = this._getSortedTaxList(tempLocal, taxHierarchies);

    // we now have a sorted list of fed/state and local codes
    // append local codes to fed/state codes and this will now be our complete master tax code list
    // this.masterTaxMap = [...sortedFedAndStateTaxCodes.concat(...sortedLocalTaxCodes)];
    return [...sortedFedAndStateTaxCodes.concat(...sortedLocalTaxCodes)];
  }

  private _getSortedTaxList(taxCodeAndTypes: TaxCodeAndType[], taxHierarchies: TaxHierarchy[]) {
    const sortedTaxes: TaxCodeAndType[] = [];

    // sort tax codes by hierarchy
    taxHierarchies.forEach(taxHierarchy => {
      // grab all the codes by it's tax type, then sort alphabetically
      const taxes = taxCodeAndTypes
        .filter(x => x.TaxType == taxHierarchy.TaxType)
        .sort((a, b) => {
          return a.TaxCode.localeCompare(b.TaxCode);
        });

      // append each of the found code(s) to our return list of codes
      // this also ensures the proper ordering
      taxes.forEach(tax => {
        sortedTaxes.push(new TaxCodeAndType(tax.TaxCode, tax.TaxType));
      });
    });

    return sortedTaxes;
  }

  public GetThirdPartyPayeeAssociations(payroll: MasterPayrollObject) {
    // loop through all deductions in all the paylines
    // and check if any have any third party payee association(s)
    let thirdPartyPayees = [];
    for (const employeePay of payroll.EmployeePays) {
      for (const payline of employeePay.Paylines) {
        for (const deduction of payline.Deductions) {
          if (deduction.ThirdPartyPayeeId) {
            // build a list of third party payees
            thirdPartyPayees.push({
              id: deduction.ThirdPartyPayeeId,
              pay: employeePay,
              deduction: deduction
            });
          }
        }
      }
    }

    return thirdPartyPayees;
  }

  private CreateDeductionMaps(deductions: CompanyDeductionConfig[]) {
    return MapBy(deductions, deduction => deduction.Id);
  }

  private CreateEarningMaps(
    earnings: CompanyEarningConfig[]
  ): Record<string, CompanyEarningConfig> {
    return MapBy(earnings, earning => earning.Id);
  }

  private _calculateTotals(line: Payline) {
    let summary = new PayrollSummary();
    summary.Totals.NetChecks = line.NetCheckAmountPaid;
    summary.Totals.DirectDeposits = line.NetAmountDeposited;
    summary.Totals.PaylineCount = 1;
    summary.Totals.Net = line.NetAmountPaid;
    return summary;
  }

  private _calculateTaxes(line: Payline) {
    let summary = new PayrollSummary();

    line.Taxes.forEach(tax => {
      if (tax.CurrentAmount != null) {
        var taxBucket;
        switch (tax.TaxPaidBy) {
          case 'C': // employer taxes "C"
            taxBucket = summary.Tax.EmployerTaxes;
            if (!summary.Tax.EmployerTaxes[tax.TaxCode]) {
              summary.Tax.EmployerTaxes[tax.TaxCode] = 0;
            }

            summary.Tax.EmployerTaxes[tax.TaxCode] += tax.CurrentAmount;
            summary.Tax.Employer += tax.CurrentAmount;

            break;
          case 'E': // employee taxes "E"
            taxBucket = summary.Tax.EmployeeTaxes;
            if (!summary.Tax.EmployeeTaxes[tax.TaxCode]) {
              summary.Tax.EmployeeTaxes[tax.TaxCode] = 0;
            }

            summary.Tax.EmployeeTaxes[tax.TaxCode] += tax.CurrentAmount;
            summary.Tax.Employee += tax.CurrentAmount;
            break;
        }

        // sum up grand total values for taxes
        summary.Tax.Total += tax.CurrentAmount;
      }
    });

    return summary;
  }

  private _calculateEarnings(
    line: Payline,
    earningConfigMap: Record<string, CompanyEarningConfig>
  ) {
    // bug 8242 - sum up total gross by the payline's EarningsTotalCurrentAmount and not by summing all earnings
    // the base sums it properly for us and takes in effect certain conditions (ie, not including REIM amounts)

    let summary = new PayrollSummary();

    summary.Earnings.Gross = line.EarningsTotalCurrentAmount;

    line.Earnings.forEach(earning => {
      if (!earningConfigMap[earning.EarningId]) return;

      const earn = earningConfigMap[earning.EarningId];

      if (!earning.CurrentAmount && !earning.Hours) {
        return;
      }

      if (!summary.Earnings.Names.includes(earn.Name)) {
        summary.Earnings.Names.push(earn.Name);
      }

      if (summary.Earnings.Totals[earn.Name]) {
        // update existing earning
        summary.Earnings.Totals[earn.Name].TotalAmount += earning.CurrentAmount;
        summary.Earnings.Totals[earn.Name].TotalHours += earning.Hours;
      } else {
        // add new earning
        let earningTotal = new EarningTotals();
        earningTotal.TotalAmount = earning.CurrentAmount;
        earningTotal.TotalHours = earning.Hours;

        summary.Earnings.Totals[earn.Name] = earningTotal;
      }

      summary.Earnings.Hours += earning.Hours;
    });

    return summary;
  }

  private _calculateDeductions(
    line: Payline,
    deductionConfigMap: Record<string, CompanyDeductionConfig>
  ) {
    var summary = new PayrollSummary();

    line.Deductions.forEach(deduction => {
      if (!deductionConfigMap[deduction.DeductionCode]) return;

      const ded = deductionConfigMap[deduction.DeductionCode];

      if (deduction.CurrentAmount != null) {
        if (ded.Type == 1) {
          // 'memo'
          if (!summary.Deductions.MemoItemDeductionTotals[ded.Name]) {
            summary.Deductions.MemoItemDeductionTotals[ded.Name] = 0;
          }

          summary.Deductions.MemoItemDeductionTotals[ded.Name] += deduction.CurrentAmount;
          summary.Deductions.DeductionsMemoTotal += deduction.CurrentAmount;
        } else {
          // 'non-memo'
          if (!summary.Deductions.EmployeeDeductionTotals[ded.Name]) {
            summary.Deductions.EmployeeDeductionTotals[ded.Name] = 0;
          }

          summary.Deductions.EmployeeDeductionTotals[ded.Name] += deduction.CurrentAmount;
          summary.Deductions.DeductionsEmployeeTotal += deduction.CurrentAmount;
        }

        // sum up grand total values for deductions
        summary.Deductions.DeductionsTotal += deduction.CurrentAmount;
      }
    });

    return summary;
  }

  private _sortTaxes(
    taxes: Record<string, number>,
    masterTaxMap: TaxCodeAndType[]
  ): TaxCodeAndTotal[] {
    let taxCodeTotals: TaxCodeAndTotal[] = [];
    // loop through our ordered master list
    // append each tax/tax amount if it exists
    masterTaxMap.forEach(masterTax => {
      if (taxes[masterTax.TaxCode]) {
        taxCodeTotals.push(new TaxCodeAndTotal(masterTax.TaxCode, taxes[masterTax.TaxCode]));
      }
    });

    return taxCodeTotals;
  }
}

export class PayrollSummary {
  public Totals = {
    NetChecks: 0,
    DirectDeposits: 0,
    PaylineCount: 0,
    Net: 0
  };

  public Tax = {
    Employer: 0,
    Employee: 0,
    Total: 0,
    EmployerTaxes: {},
    EmployeeTaxes: {}
  };

  public Earnings = {
    Gross: 0,
    Hours: 0,
    Names: [],
    //TODO - Earnings.Totals should be type-safed
    Totals: {}
  };

  public Deductions = {
    DeductionsMemoTotal: 0,
    DeductionsEmployeeTotal: 0,
    DeductionsTotal: 0,
    //these should be type safed
    EmployeeDeductionTotals: {},
    MemoItemDeductionTotals: {}
  };

  constructor() {}

  public concat(other: PayrollSummary) {
    this.Totals.NetChecks += other.Totals.NetChecks;
    this.Totals.DirectDeposits += other.Totals.DirectDeposits;
    this.Totals.PaylineCount += other.Totals.PaylineCount;
    this.Totals.Net += other.Totals.Net;

    this.Tax.Employer += other.Tax.Employer;
    this.Tax.Employee += other.Tax.Employee;
    this.Tax.Total += other.Tax.Total;

    this.Earnings.Gross += other.Earnings.Gross;
    this.Earnings.Hours += other.Earnings.Hours;

    this.Deductions.DeductionsMemoTotal += other.Deductions.DeductionsMemoTotal;
    this.Deductions.DeductionsTotal += other.Deductions.DeductionsTotal;
    this.Deductions.DeductionsEmployeeTotal += other.Deductions.DeductionsEmployeeTotal;

    this.Deductions.EmployeeDeductionTotals = mergeSum(
      this.Deductions.EmployeeDeductionTotals,
      other.Deductions.EmployeeDeductionTotals
    );
    this.Deductions.MemoItemDeductionTotals = mergeSum(
      this.Deductions.MemoItemDeductionTotals,
      other.Deductions.MemoItemDeductionTotals
    );

    // merge both string arrays removing duplicates
    //
    // todo - update when JS has a sane way to remove duplicates
    // this works for primitive types but probably breaks for objects --bbarrett
    this.Earnings.Names = [...new Set([...this.Earnings.Names, ...other.Earnings.Names])];

    this.Earnings.Totals = mergeSum(this.Earnings.Totals, other.Earnings.Totals);

    this.Tax.EmployerTaxes = mergeSum(this.Tax.EmployerTaxes, other.Tax.EmployerTaxes);
    this.Tax.EmployeeTaxes = mergeSum(this.Tax.EmployeeTaxes, other.Tax.EmployeeTaxes);
  }
}

export class PayrollSummaryOrderedLists {
  public EmployerTaxes: TaxCodeAndTotal[];
  public EmployeeTaxes: TaxCodeAndTotal[];
  public EarningsNames: string[];
  public EmployeeDeductionNames: string[];
  public MemoDeductionNames: string[];
}

export class TaxCodeAndTotal {
  constructor(code: string, amount: number) {
    this.TaxCode = code;
    this.Amount = amount;
  }

  public TaxCode: string;
  public Amount: number;
}

export class TaxCodeAndType {
  constructor(code: string, type: string) {
    this.TaxCode = code;
    this.TaxType = type;
  }

  public TaxCode: string;
  public TaxType: string;
}
